/**
 * ModuleManifestRegistry — captures the gateway's `/api/v1/ui/manifest` module list
 * so non-shell consumers (HomePage, Plant Canvas, ⌘K palette, settings pages)
 * can iterate every module Tyndo knows about without re-fetching or hardcoding.
 *
 * Pairs with the existing MenuRegistry / ViewRegistry / ActionRegistry — those
 * decompose the manifest into navigation primitives; this one keeps the
 * module-level identity (id, name, icon, color, federation name) intact.
 */

import type { Isa95Tier, ProductFamily } from '@tyndo/types';
import type { IsaGroup } from '../layout/isaGroups';

export interface ModuleManifestEntry {
 moduleId: string;
 name: string;
 version?: string;
 federatedModuleName?: string;
 icon?: string;
 color?: string | null;
 /** Optional description; not always present in the gateway manifest. */
 description?: string;
 /** Module-level API prefixes ("/api/v1/integration"). */
 apiPrefixes?: string[];
 /**
  * ISA-95 functional tier ("level-0"…"level-4" | "platform"). Carried through
  * from the gateway manifest (preserved by the parser's `...raw` spread) and
  * consumed by the sidebar's ISA-95 bucketing so a plant-floor module lands in
  * its real operate/plan group instead of the Admin catch-all.
  */
 isa95Tier?: Isa95Tier;
 /**
  * Product family (per docs/module-categorization.md). Same provenance as
  * `isa95Tier`; the sidebar prefers this over tier for the coarse family →
  * ISA group mapping.
  */
 productFamily?: ProductFamily;
 /**
  * Sidebar group the gateway already resolved from the canonical taxonomy
  * (`isaGroup` in the UI manifest). Authoritative when present — the sidebar's
  * `inferIsaGroup` prefers it over the raw tier/family because the doc
  * taxonomy, not the noisy family field, decides the bucket (e.g. Materials is
  * `mes-mom` but belongs in `plan`). Absent for un-migrated modules, which
  * keep the substring fallback.
  */
 isaGroup?: IsaGroup;
 /**
  * Widget descriptors carried through from the gateway manifest (preserved by
  * the parser's `...raw` spread). Left as `unknown[]` — the registry itself
  * never interprets them; the entitlement evaluator only reads each entry's
  * `widgetId` (with a runtime string guard) so the shape stays a caller
  * concern rather than a fixed contract here.
  */
 widgets?: unknown[];
}

/**
 * Validate that an icon name a manifest declares actually resolves to a
 * lucide-react export. Sidebar/nav rendering resolves icons by name at runtime
 * and silently falls back to a neutral Circle when the name is unknown, which
 * makes a typo'd manifest icon ("Wrenchh", "factory" with wrong case) hard to
 * spot. Surfacing it at registration turns a silent fallback into an actionable
 * warning. Supported names are the PascalCase keys of lucide-react's `icons`
 * export (e.g. "Boxes", "Factory", "Wrench"). Returns the offending names.
 */
export function unknownIconNames(
 modules: ModuleManifestEntry[],
 iconExists: (name: string) => boolean,
): Array<{ moduleId: string; icon: string }> {
 const bad: Array<{ moduleId: string; icon: string }> = [];
 for (const m of modules) {
 if (m.icon && !iconExists(m.icon)) {
 bad.push({ moduleId: m.moduleId, icon: m.icon });
 }
 }
 return bad;
}

class ModuleManifestRegistry {
 private modules = new Map<string, ModuleManifestEntry>();
 /**
  * Optional icon-name validator, injected by the shell at bootstrap (it owns
  * the lucide-react dependency). When set, register() warns on any manifest
  * icon that won't resolve, instead of letting the nav silently fall back.
  */
 private iconExists: ((name: string) => boolean) | null = null;

 /** Wire the lucide icon-name validator used during register(). */
 setIconValidator(fn: (name: string) => boolean): void {
 this.iconExists = fn;
 }

 register(modules: ModuleManifestEntry[]): void {
 if (this.iconExists) {
 const bad = unknownIconNames(modules, this.iconExists);
 if (bad.length > 0) {
 console.warn(
 `[ModuleManifestRegistry] Unknown lucide-react icon name(s) in manifest — nav will fall back to a neutral marker: ${bad
 .map((b) => `${b.moduleId} → "${b.icon}"`)
 .join(', ')}`,
 );
 }
 }
 for (const m of modules) {
 if (m.moduleId) {
 this.modules.set(m.moduleId, m);
}
}
}

 get(moduleId: string): ModuleManifestEntry | undefined {
 return this.modules.get(moduleId);
}

 list(): ModuleManifestEntry[] {
 return Array.from(this.modules.values());
}

 clear(): void {
 this.modules.clear();
}
}

export const moduleManifestRegistry = new ModuleManifestRegistry();
